﻿<template>
    <DataTable
        id="users"
        :url="dtUrl"
        :columns="columns"
        :orderBy="`email`"
        :order-dir="`asc`"
        :filters="appliedFilters"
        :loaderEnabled="true"
        :refresh="refreshTable"
        :useLocalStorage="false"
        :searchingEnabled="true"
        :hideTableActions="true"
        :showExportCsv="true"
        :exportCsvCallback="exportCsvCallback"
        @fetchingData="setRefreshFalse"
        @update:modelValue="handleModelUpdate"
    >

    <template #filters>
        <div class="col-auto">
            <AppButton
                btnClass="btn-white"
                @click="() => isFiltersVisible = true"
            >
                <i class="bi-filter me-1"></i> {{ $t('Filters') }} <span class="badge bg-soft-dark text-dark rounded-circle ms-1">{{ filtersCount }}</span>
            </AppButton>
        </div>
        <div class="col-auto" v-if="filtersCount >= 1">
            <AppButton
                btnClass="btn-white"
                @click="resetFilters"
            >
                <i class="bi-arrow-counterclockwise"></i> {{ $t('Reset') }}
            </AppButton>
        </div>
        <AppOffcanvas
            :title="$t('Filters')"
            :isVisible="isFiltersVisible"
            @update:is-visible="isFiltersVisible = $event"
        >
            <template #body>
                <div class="row">
                    <div class="col-12 mb-4">
                <div class="col-12">
                    <span class="form-label text-cap text-body">{{ $t('User Created Start') }}</span>
                    <AppDatepicker
                        :placeholder="$t('User Created Start')"
                        v-model="userCreatedStartDate"
                        :readonly="loading"
                    />
                </div>

                <div class="col-12">
                    <span class="form-label text-cap text-body">{{ $t('User Created End') }}</span>
                    <AppDatepicker
                        :placeholder="$t('User Created End')"
                        v-model="userCreatedEndDate"
                        :readonly="loading"
                    />
                </div>

                    </div>
                </div>
                <div class="row">
                    <div class="col-12 mb-4">
                        <label for="active" class="form-label text-cap text-body">{{ $t('Status') }}</label>

                        <select
                            name="active"
                            id="active"
                            class="form-select"
                            tabindex="-1"
                            v-model="formValues.active"
                        >
                            <option value="" selected>{{ $t('All') }}</option>
                            <option value="0">{{ $t('Inactive') }}</option>
                            <option value="1">{{ $t('Active') }}</option>
                            <option value="2">{{ $t('Invited') }}</option>
                            <option value="3">{{ $t('Expired') }}</option>
                            <option value="4">{{ $t('Locked') }}</option>
                        </select>
                    </div>
                </div>

                <div class="row">
                    <div class="col-12 mb-4">
                        <label for="user_type" class="form-label text-cap text-body">{{ $t('User Type') }}</label>

                        <select
                            name="user_type"
                            id="user_type"
                            class="form-select"
                            tabindex="-1"
                            v-model="formValues.user_type"
                        >
                            <option value="" selected>{{ $t('All') }}</option>
                            <option class="form-control" value="Organization">{{ $t('Organization') }}</option>
                            <option class="form-control" value="Group">{{ $t('Group') }}</option>
                            <option class="form-control" value="Location">{{ $t('Location') }}</option>
                        </select>
                    </div>
                </div>
                <div class="row">
                    <div class="col-12 mb-4">
                        <label for="user_type" class="form-label text-cap text-body">{{ $t('Vent Access') }}</label>

                        <select
                            name="vent_access"
                            id="vent_access"
                            class="form-select"
                            tabindex="-1"
                            v-model="formValues.vent_access"
                        >
                            <option value="" selected>{{ $t('All') }}</option>
                            <option class="form-control" value="Enabled">{{ $t('Enabled') }}</option>
                            <option class="form-control" value="Disabled">{{ $t('Disabled') }}</option>
                        </select>
                    </div>
                </div>
            </template>
            <template #footer>
                <div class="col">
                    <div class="d-grid">
                        <AppButton @click="cleanForm" btnClass="btn-white">
                            <i class="bi bi-arrow-counterclockwise"></i> {{ $t('Clear') }}
                        </AppButton>
                    </div>
                </div>

                <div class="col">
                    <div class="d-grid">
                        <AppButton @click="applyFilters">
                            <i class="bi bi-check-square"></i> {{ $t('Apply') }}
                        </AppButton>
                    </div>
                </div>
            </template>
        </AppOffcanvas>
    </template>
    </DataTable>

    <!-- Modal -->
    <app-modal :id="'org-user-index'" v-model="editUserModalVisible" :size="`lg`">
        <div class="mb-9">
            <CreateUser
                ref="createUserRef"
                :auth_user="auth_user"
                :selected-location="currentUser.org_location_id"
                :user="currentUser"
                :org_id="currentUser.org_id"
                :is_org_type_clinical="is_org_type_clinical"
                @user-error="userError"
                @user-success="userSuccess"
                @user-saving-canceled="userCancel"
                :org_locations="org_locations"
                :org_groups="org_groups"
                :org_settings="org_settings"
                :key="currentUser.org_id" />
        </div>
        <template #header>
            <h3 class="modal-title">{{ userModalTitle }}</h3>
        </template>

        <template #footer>
            <AppButton btnClass="btn-white"  @click="() => editUserModalVisible = false">
                <i class="bi bi-x-lg me-1"></i> {{ $t('Close') }}
            </AppButton>
            <AppButton btnClass="btn-primary" @click="updateUser(currentUser)" :is-loading="savingUser">
                <i class="bi bi-save me-1"></i> {{ $t('Save') }}
            </AppButton>
        </template>
    </app-modal>

    <app-modal v-model="passwordModalVisible">
        <template #header>
            <h3 class="modal-title">{{ $t('Reset Password') }}</h3>
        </template>
        <div class="row">
            <div class="mb-3 col-md-9">
                <label class="fs-6 mb-0 --form-label --mb-1" for="password">{{ $t('New Password') }}</label>
                <input type="password" autocomplete="off" :placeholder="$t('New Password')" class="form-control form-control-sm" v-model="currentUser.password" name="password" id="password" />
            </div>
        </div>

        <div class="row">
            <div class="mb-3 col-md-9">
                <label class="fs-6 mb-0 --form-label --mb-1" for="password_confirmation">{{ $t('Confirm Password') }}</label>
                <input type="password" autocomplete="off" :placeholder="$t('Confirm Password')" class="form-control form-control-sm" v-model="currentUser.password_confirmation" name="password_confirmation" id="password_confirmation" />
            </div>
        </div>

        <template #footer>
            <AppButton btnClass="btn-white" @click="closeResetPasswordModal(currentUser)">
                <i class="bi bi-x-lg me-1"></i> {{ $t('Close') }}
            </AppButton>
            <AppButton btnClass="btn-primary" @click="saveUser(currentUser)">
                <i class="bi bi-save me-1"></i> {{ $t('Save') }}
            </AppButton>
        </template>
    </app-modal>

    <app-modal v-model="displayUserLoginModal" :size="`xl`" :key="currentUser.id" >
        <template #header>
            <h3 class="modal-title">{{ $t('Login History') }}</h3>
        </template>
        <div class="row">
            <LoginHistory
                ref="loginHistoryRef"
                :user_id="currentUser.id"
                v-if="userLoginHistoryVisible && currentUser.id"
            />
        </div>
        <template #footer>
            <AppButton btnClass="btn-white" @click="() => displayUserLoginModal = false">
                <i class="bi bi-x-lg me-1"></i> {{ $t('Close') }}
            </AppButton>
        </template>
    </app-modal>

    <app-modal v-model="displayUserAuditLogModal"  :size="`xl`" :key="currentUser.id" >
        <template #header>
            <h3 class="modal-title">{{ $t('User Audit Log') }}</h3>
        </template>
        <div class="row">
            <AuditLog v-if="userAuditLogVisible" ref="auditLogRef" :user_id="currentUser.id"
            ></AuditLog>
        </div>
        <template #footer>
            <AppButton btnClass="btn-white" @click="() => displayUserAuditLogModal = false">
                <i class="bi bi-x-lg me-1"></i> {{ $t('Close') }}
            </AppButton>
        </template>
    </app-modal>

    <app-modal v-model="display2FAResetModal" :key="currentUser.id">
        <template #header>
            <h3 class="modal-title">{{ $t('Reset 2FA') }}</h3>
        </template>
        <div class="row w-full">
            <div v-if="org_settings.auth && org_settings.auth['2fa_app']" class="d-flex align-items-center justify-content-between mb-2">
                <span>{{ $t('auth.2fa.app.header') }}</span>
                <AppButton
                    v-if="currentUser.two_factor_app_enabled"
                    btnClass="btn-sm btn-soft-primary"
                    :is-loading="isProcessing2FA"
                    @click.prevent="handleReset2FA('app')">
                    {{ $t('Reset') }}
                </AppButton>
                <div v-else class="text-sm text-muted">{{ $t('Not configured') }}</div>
            </div>
            <div v-if="org_settings.auth && org_settings.auth['2fa_sms']" class="d-flex align-items-center justify-content-between mb-2">
                <span>{{ $t('auth.2fa.sms.header') }}</span>
                <AppButton
                    v-if="currentUser.two_factor_sms_enabled"
                    btnClass="btn-sm btn-soft-primary"
                    :is-loading="isProcessing2FA"
                    @click.prevent="handleReset2FA('sms')">
                    {{ $t('Reset') }}
                </AppButton>
                <div v-else class="text-sm text-muted">{{ $t('Not configured') }}</div>
            </div>
        </div>
    </app-modal>

    <ApiKeyManagementModal
        :user="currentUser"
        :is-visible="apiKeyManagerModalVisible"
        @update:modelValue="(value) => apiKeyManagerModalVisible = value"
        @on-generated="reloadTable"
        @on-regenerated="reloadTable"
        @on-revoked="reloadTable"
    />

    <RolePermissionModal
        v-model="roleModalVisible"
        :roleId="roleId"
        :key="roleId"
    />

    <ActivityLogModal
        :user="currentUser"
        v-model="displayActivityLogModal"
    />
</template>

<script setup lang="ts">
import axios from "axios";
import AppModal from "@/vue/shared/AppModal.vue";
import AppButton from "@/vue/shared/AppButton.vue";
import AuditLog from "./AuditLog.vue";
import CreateUser from "./create.vue";
import {
    computed,
    defineExpose,
    defineProps,
    onMounted,
    ref,
    watch,
    inject,
    onUnmounted,
    h,
    render,
    nextTick
} from "vue";
import { toast } from "vue3-toastify";
import "vue3-toastify/dist/index.css";
import LoginHistory from "./LoginHistory.vue";
import useDatesDeprecated from "@/vue/composables/useDates";
import usePermissions from "@/vue/composables/usePermissions";
import { cloneDeep } from "lodash";
import {trans as $t} from "@/vue/composables/missingTranslations";
import DataTable from "@/vue/datatable/DataTable.vue";
import AppOffcanvas from "@/vue/shared/AppOffcanvas.vue";
import moment from "moment/moment";
import AppDatepicker from "@/vue/shared/AppDatepicker.vue";
import ApiKeyManagementModal from "@/vue/connect/users/modal/ApiKeyManagementModal.vue";
import { createAlertDialog, createConfirmDialog } from "@/vue/helpers";
import OptionsList from "@/vue/connect/users/actions/OptionsList.vue";
import RolePermissionModal from "@/vue/connect/users/modal/RolePermissionModal.vue";
import { useUserStore } from "@/vue/store/userStore";
import ActivityLogModal from "@/vue/connect/users/modal/ActivityLogModal.vue";
import useDates from "@/libs/dates/useDates";

const {reveal: confirm} = createConfirmDialog();
const {reveal: alert} = createAlertDialog();

const userStore = useUserStore();
const can = userStore.can;

const user = computed(() => userStore.user);
const org = computed(() => userStore.org);

const canExport = computed(() => {
    return can("export.user");
});

const refreshTable = ref(false);

const props = defineProps({
    org_id: {
        type: Number,
        required: false,
        default: null
    },
    is_org_type_clinical: {
        type: Boolean,
        required: false,
        default: false
    },
    scope: {
        type: Array,
        required: false,
        default: ['viewAll.user']
    },
    org_native_login_enabled: {
        type: Boolean,
        required: false,
        default: true
    },
    auth_user: {
        type: Object,
        required: true,
    },
    org_locations: {
        type: Array,
        required: false,
        default: []
    },
    org_groups: {
        type: Array,
        required: false,
        default: []
    },
    org_settings: {
        type: Object,
        required: false,
        default: []
    }
});

const dtUrl = props.org_id === null ? ref("/api/users/datatable") : ref('/api/orgs/' +  props.org_id  + '/users/datatable');

const { getFormattedDate } = useDatesDeprecated('MM/DD/YYYY');
const { format } = useDates();

const currentUser = ref({
    org_id: props.org_id,
    user_type: [{}],
    roles: [{}],
    can: {},
    password: '',
    password_confirmation: '',
    roleLocations: [],
    roleGroups: [],
});


const editUserModalVisible = ref(false);
const userModalTitle = ref($t("Edit User"));
const userLoginHistoryVisible = ref(false);
const userAuditLogVisible = ref(false);
const displayUserLoginModal = ref(false);
const displayUserAuditLogModal = ref(false);
const displayActivityLogModal = ref<boolean>(false);

const createUserRef = ref();
const savingUser = ref(false);
const loading = ref(false);
const users = ref({});

const columns = computed(() => {
    return [
        { data: "", title: "", visible:true, render: renderItemOptions, className: 'dt-control', orderable: false, sortable: false, defaultContent: '', columnSelector: false },
        { data: "active", visible: true, render: displayActive, title: $t("Status"), columnSelector: false },
        { data: "id", visible: false },
        { data: "email", title: $t("E-mail") },
        { data: "last_successful_login", title: $t("Last Successful Login"), render: (data) => data ? format(data, 'MM/DD/YYYY HH:mm:ss') : '' },
        { data: "user_type", visible:true, title: $t("Type"), render: renderUserType },
        { data: "first_name", title: $t("Full Name"), render: fullName },
        { data: "phone", title: $t("Mobile Phone"), render: formatPhoneNumber },
        { data: "roles", visible:true, title: $t("Role"), render: renderRole },
        { data: "created", title: $t("Created"), render: (data) => moment(data).format('MM/DD/YYYY') },
    ];
});


const apiKeyManagerModalVisible = ref(false);
const roleId = ref();
const roleModalVisible = ref(false);
const passwordModalVisible = ref(false);
const userTypes = ref({});
const filtersApplied = ref(false);
const userCreatedEndDate = ref('');
const userCreatedStartDate = ref('');
const defaultFilters = {
    active: '',
    user_type: '',
    vent_access: '',
    date: {
        start: '',
        end: ''
    },
};


const formValues = ref(cloneDeep(defaultFilters));
const appliedFilters = ref(cloneDeep(defaultFilters));
const isFiltersVisible = ref(false);
const filtersCount = computed(() => {
    return Object.keys(appliedFilters.value).reduce((count, key) => {
        const appliedValue = appliedFilters.value[key];
        const defaultValue = defaultFilters[key];

        let isDifferent;

        if (typeof appliedValue === "object") {
            isDifferent = JSON.stringify(appliedValue) !== JSON.stringify(defaultValue);
        } else {
            isDifferent = appliedValue !== defaultValue;
        }

        return count + (isDifferent ? 1 : 0);
    }, 0);
});

function applyFilters() {
    appliedFilters.value = cloneDeep(formValues.value);
    isFiltersVisible.value = false;
}

function cleanForm() {
    formValues.value = cloneDeep(defaultFilters);
}

function resetFilters() {
    cleanForm();
    applyFilters();
}

watch(userCreatedStartDate, async () => {
    formValues.value.date.start = moment(userCreatedStartDate.value).format("YYYY-MM-DD");
});

watch(userCreatedEndDate, async () => {
    formValues.value.date.end = moment(userCreatedEndDate.value).format("YYYY-MM-DD");
});
const isMounted = ref(false);

const sharedState = inject('sharedState');


watch(appliedFilters, (newValue) => {
    localStorage.setItem("appliedFilters", JSON.stringify(newValue));
});


const displayToast = (success, msg) => {
    if (success) {
        toast.success(msg, {
            position: 'bottom-center'
        });
    } else {
        toast.error(msg, {
            position: 'bottom-center'
        });
    }
}

const editUser = async (user) => {
    currentUser.value = user;
    if (user.id) {
        if (props.org_id === null) {
            await Promise.all([
                createUserRef.value.getLocations(currentUser.value.org_id),
                createUserRef.value.getGroups(currentUser.value.org_id)
            ]);
        }
        createUserRef.value.formatUserLocationsGroups(user);
        createUserRef.value.setOriginalLocations(cloneDeep(user.roleLocations));
        createUserRef.value.setOriginalGroups(cloneDeep(user.roleGroups));
        if (user.roles && user.roles.length > 0) {
            createUserRef.value.setOriginalRole(cloneDeep(user.roles[0].id));
        }

        if (user.user_type && user.user_type.length > 0) {
            createUserRef.value.setOriginalUserType(cloneDeep(user.user_type[0].id));
        }
        userModalTitle.value = $t("Edit User");
        currentUser.value.phone = user.phone = formattedPhoneNumberForEdit(user.phone);
        currentUser.value.locationsChanged = false;
        currentUser.value.groupsChanged = false;
    } else {
        currentUser.value.is_clinician = true;
        currentUser.value.email = '';
        currentUser.value.confirm_email = '';
        currentUser.value.first_name = '';
        currentUser.value.last_name = '';
        currentUser.value.phone = '';
        userModalTitle.value = $t("Create User");
    }
    createUserRef.value.resetErrors();
    createUserRef.value.resetUserTypes();
    editUserModalVisible.value = true;
}

const uploadFile = async (file) => {
    let formData = {};
    loading.value = true;
    formData.file =  file;
    formData.org_id = props.org_id;

    await axios.post('/api/orgs/' + props.org_id + '/users/import', formData, {
        headers: {
            'Content-Type': 'multipart/form-data'
        }
    }).then(response => {
        if (response.hasOwnProperty('response') && response.response.hasOwnProperty('status') && response.response.status === 422) {
            loading.value = false;
            if (response.response.data.validation_error) {
                displayToast(false, formatErrors(response.response.data));
            } else {
                displayToast(false, response.response.data);
            }
            reloadTable();
        } else {
            if (response.status === 201 || response.status === 200) {
                loading.value = false;
                displayToast(true, response.data.message);
            }
        }
    }).catch(error => {
        loading.value = false;
        if (error.response.data.validation_error) {
            displayToast(false, formatErrors(error.response.data));
        } else {
            displayToast(false, error.response.data);
        }

    }).finally(() => {
        loading.value = false;
    });
};

defineExpose({ editUser, uploadFile });

const formattedPhoneNumberForEdit = (phone) => {
    if (phone == null) return '';
    const digits = phone.replace(/\D/g, '');

    if (digits.length === 0) {
        return phone;
    }

    const areaCode = digits.slice(0, 3);
    const firstPart = digits.slice(3, 6);
    const secondPart = digits.slice(6);

    return `${areaCode}-${firstPart}-${secondPart}`;
}

const display2FAResetModal = ref(false);
const isProcessing2FA = ref(false);
const handleReset2FA = async (method: 'sms' | 'app') => {
    const response = await confirmWindow(
        $t("Warning!"),
        $t("This authentication method will have to be reconfigured by the user. Are you sure you want to continue?"),
    );
    if (!response.isCanceled) {
        isProcessing2FA.value = true;

        try {
            const response = await axios.post('/api/users/reset-mfa/' + currentUser.value.id, { method });

            if (response.hasOwnProperty('response') && response.response.hasOwnProperty('status') && response.response.status === 422) {
                displayToast(false, response.response.data);
            }
            else if (response.status === 201 || response.status === 200) {
                displayToast(true, $t("2FA successfully reset"));
            }
            display2FAResetModal.value = false;
            await reloadTable();
        } catch (e) {
            console.error(e);
            displayToast(false, e.message);
        } finally {
            isProcessing2FA.value = false;
        }
    }
}

const toggleUser = async (user) => {
    const confirmationMessage = user.active === 1
        ? $t("Are you sure you want to disable the user?")
        : $t("Are you sure you want to enable the user?");
    const response = await confirmWindow($t("Warning!"), confirmationMessage);
    if (!response.isCanceled) {
        user.active = user.active === 1 ? 0 : 1;
        delete user.newRole;
        delete user.password;
        saveUser(user);
    }
}
const manageApiKey = async () => {
    const isOrgLevel = currentUser.value?.roles.some(role => [
        "org.ep.admin", "org.clinical.admin",
        "org.ep.staff", "org.clinical.staff"
    ].includes(role.name));

    if (!isOrgLevel) {
        await alert({
            "title": $t("Warning!"),
            "message": $t("API keys are only available for Org-level users")
        });

        return;
    }

    apiKeyManagerModalVisible.value = true;
};

const resetPassword = () => {
    passwordModalVisible.value = true;
}

const closeResetPasswordModal = (user) => {
    passwordModalVisible.value = false;
    user.password = '';
    user.password_confirmation = '';
}

const showLoginHistory = () => {
    userLoginHistoryVisible.value = true;
    displayUserLoginModal.value = true;
}

const showActivityLog = () => {
    displayActivityLogModal.value = true;
}

const showAuditLog = () => {
    userAuditLogVisible.value = true;
    displayUserAuditLogModal.value = true;
}

const unlockUser = async user => {
    const response = await confirmWindow($t("Warning!"), $t("Are you sure you want to unlock the user?"));
    if (!response.isCanceled) {
        user.unlock = 1;
        delete user.password;
        saveUser(user);
    }
    delete user.unlock;
}

const resendInvitation = async user => {
    const response = await confirmWindow($t("Warning!"), $t("Are you sure you want to resend the invitation?"));
    if (!response.isCanceled) {
        user.resend_email = 1;
        delete user.password;
        saveUser(user);
    }
    delete user.resend_email;
}

const toggleVentilatorAccess = async (user, enabled) => {
    const confirmationMessage = enabled
        ? $t("Are you sure you want to enable access to the ventilator patients for this user?")
        : $t("Are you sure you want to disable access to the ventilator patients for this user?");
    const response = await confirmWindow($t("Warning!"), confirmationMessage);
    if (!response.isCanceled) {
        await saveUserSetting(user, enabled);
    }
};

const saveUserSetting = async (user, enabled) => {
    try {
        const mvc_settings = {
            ...user.mvc_settings,
            enabled,
        };
        const response = await axios.patch(
            `/api/users/${user.id}/mvc-settings`,
            { mvc_settings },
        );
        if (response.status === 200 || response.status === 201) {
            const message = enabled
                ? "Access to ventilator patients enabled successfully!"
                : "Access to ventilator patients disabled successfully!";
            displayToast(true, $t(message));
            reloadTable();
        }
    } catch (error) {
        displayToast(false, error.response.data);
    }
};

const phoneNumber = ref();
watch(currentUser, () => {
    phoneNumber.value = currentUser.value.phone
})
const updateUser = async (user) => {
    if (user.two_factor_sms_enabled && user.phone !== phoneNumber.value) {
        const confirmed = await confirmWindow($t("Warning!"), $t('messages.2fa_changed_confirmed_phone'));
        if (confirmed.isCanceled === true) {
            return
        }
    }
    if (props.org_settings.auth && props.org_settings.auth['2fa_enabled'] && props.org_settings.auth['2fa_prevent_phone_numbers'] && !user.phone) {
        const response = await confirmWindow($t("Warning!"), $t("messages.2fa_user_doesnt_have_phone"));
        if (!response.isCanceled) {
            await saveUser(user);
        }
    } else {
        await saveUser(user);
    }
}

const saveUser = async (user) => {
    savingUser.value = true;
    await createUserRef.value.createUser(user);
}

const showUserModal = () => {
    editUserModalVisible.value = true;
}
const closeUserModal = () => {
    editUserModalVisible.value = false;
    passwordModalVisible.value = false;
    displayUserLoginModal.value = false;
    displayUserAuditLogModal.value = false;
    display2FAResetModal.value = false;
}

const userSuccess = response => {
    closeUserModal();
    savingUser.value=false;
    createUserRef.value.resetUser();
    reloadTable();
    displayToast(true, $t("User :email successfully updated", { email: response.email }));
}

const userCancel = () => {
    savingUser.value = false;
}

const userError = response => {
    savingUser.value= false;
    displayToast(false, response.message);
}

const { hasPermission } = usePermissions();

const reloadTable = async () => {
    refreshTable.value = true;

    setTimeout(() => refreshTable.value = false, 1000);
}

const setRefreshFalse = (bool) => {
    if (!bool) {
        refreshTable.value = false;
    }
}

const handleModelUpdate = (newValue) => {
    users.value = newValue;
}
const formatErrors = (errorResponse) => {
    let errorMessage = '';

    errorResponse.validation_error.forEach(errorItem => {
        errorMessage += `Row ${errorItem.row}, ${errorItem.attribute}: ${errorItem.errors.join(', ')}. `;
    });

    return errorMessage;
};

function fullName(row, context) {
    return context.first_name + " " + context.last_name;
}

function renderItemOptions(row, context) {
    const hasCreatePermission = ref(hasPermission(context.can, "create"));
    const hasUpdatePermission = ref(hasPermission(context.can, "update"));
    const hasViewPermission = ref(hasPermission(context.can, "view"));

    const container = document.createElement('div');

    render(h(OptionsList, {
        hasCreatePermission: hasCreatePermission,
        hasUpdatePermission: hasUpdatePermission,
        hasViewPermission: hasViewPermission,
        context: context,
        org_settings: props.org_settings,
        auth_user: props.auth_user
    }), container);

    return container.innerHTML;
}

function displayActive(row, context) {
    let hasApi = context.has_api === 1 ? '<i class="bi bi-key"></i>' : '';

    if (context.email_verified_at === null) {
        return `<i class="bi bi-envelope"></i> ${hasApi}`;
    }

    if (context.locked === 1) {
        return `<i class="bi bi-lock text-danger"></i> ${hasApi}`;
    }

    let isActive = context.active ? `<span style="color: #88BC1F; font-size: 20px;">●</span>` : `<span  style="color: #AEAEAE; font-size: 20px;">●</span>`;

    return `${isActive} ${hasApi}`;
}


function renderUserType(row, context) {
    if (context.user_type && context.user_type.length > 0) {
        return context.user_type[0].name;
    }
}

function renderRole(row, context) {
    const role = context.roles?.[0];
    if (!role) return;

    return hasPermission(user.value.can, "viewAll.role")
        ? `<a href="#" class="user-role" data-id="${role.id}">${role.label}`
        : role.label;
}

function formatPhoneNumber(row, context) {
    if (context.phone == null) return '';
    const digits = context.phone.replace(/\D/g, '');

    if (digits.length === 0 || digits.length < 10) {
        return context.phone;
    }

    const countryCode = '+1';
    const areaCode = digits.slice(0, 3);
    const firstPart = digits.slice(3, 6);
    const secondPart = digits.slice(6);

    return `${countryCode} (${areaCode}) ${firstPart}-${secondPart}`;
}


const confirmWindow = async (title, message) => {
    return await confirm({
            title: title,
            message: message,
            confirmButtonText: $t("Ok"),
            cancelButtonText: $t("Close")
        }
    );
}

const exportCsvCallback = async function() {
    let setting = org.value.settings.settings?.export_to_csv_access;

    if (setting === 0) { // None
        await alert({
            "title": $t("Disabled action"),
            "message": $t("The option to Export to .CSV has been disabled by your Admin")
        });

        return false;
    }

    if (setting === 2 && !canExport.value) { // Admin-only
        await alert({
            "title": $t("Disabled action"),
            "message": $t("This action is restricted to Admin users only")
        });

        return false;
    }

    return true;
};

onMounted(() => {
    const dataTableElement = document.querySelector('#users');
    dataTableElement?.addEventListener('click', handleUserControlsUserClick);

    if (isMounted.value) {
        userTypes.value = createUserRef.value.userTypes;

        return;
    }

    isMounted.value = true;
});

onUnmounted(() => {
    const dataTableElement = document.querySelector('#users');

    if (dataTableElement) {
        dataTableElement.removeEventListener('click', handleUserControlsUserClick);
    }
});

const handleUserControlsUserClick = (event) => {
    if (event.target && (event.target.classList.contains("dropdown-item") || event.target.classList.contains("dropdown-item-icon"))) {
        const target = event.target;
        currentUser.value = getUserById(parseInt(target.getAttribute("data-id")));

        if (!currentUser.value) {
            currentUser.value = getUserById(parseInt(target.parentNode.getAttribute("data-id")));
        }

        if (target.classList.contains("edit-org-user") || target.classList.contains("edit-org-user-icon")) {
            event.preventDefault();
            editUser(currentUser.value);
        } else if (target.classList.contains("org-user-unlock") || target.classList.contains("org-user-unlock-icon")) {
            event.preventDefault();
            unlockUser(currentUser.value)
        } else if (target.classList.contains("org-user-resend-invitation") || target.classList.contains("org-user-resend-invitation-icon")) {
            event.preventDefault();
            resendInvitation(currentUser.value)
        } else if (target.classList.contains("org-user-reset-2fa") || target.classList.contains("org-user-reset-2fa-icon")) {
            event.preventDefault();
            nextTick(() => display2FAResetModal.value = true);
        } else if (target.classList.contains("org-user-toggle") || target.classList.contains("org-user-toggle-icon")) {
            event.preventDefault();
            toggleUser(currentUser.value);
        } else if (target.classList.contains("org-user-reset-password") || target.classList.contains("org-user-reset-password-icon")) {
            event.preventDefault();
            resetPassword();
        } else if (target.classList.contains("org-user-manage-api-key") || target.classList.contains("org-user-manage-api-key-icon")) {
            event.preventDefault();
            manageApiKey();
        } else if (target.classList.contains("org-user-login-history") || target.classList.contains("org-user-login-history-icon")) {
            event.preventDefault();
            showLoginHistory();
        } else if (target.classList.contains("org-user-enable-mvc-access") || target.classList.contains("org-user-enable-mvc-access-icon")) {
            event.preventDefault();
            toggleVentilatorAccess(currentUser.value, !currentUser.value.mvc_settings?.enabled);
        } else if (target.classList.contains("org-user-disable-mvc-access") || target.classList.contains("org-user-disable-mvc-access-icon")) {
            event.preventDefault();
            toggleVentilatorAccess(currentUser.value, !currentUser.value.mvc_settings?.enabled);
        } else if (target.closest(".user-activity-log")) {
            event.preventDefault();
            showActivityLog();
        }
    }


    if (event.target && event.target.closest('.user-role')) {
        roleId.value = parseInt(event.target.getAttribute("data-id"))
        roleModalVisible.value = true;
    }
}

const getUserById = (userId) => {
    return cloneDeep(users.value.find(user => user.id === userId));
}

</script>

<style scoped>

.form-check label {
    display: block ruby;
}
</style>




